// Fix for SummaryTab.tsx
import React, { useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import type { Project, User, Item, FundEntry } from '../types/core';
import type { Attachment } from '../types/attachments';
import type { LanguageContextType } from '../translations';
import type { NotificationContextType } from '../contexts/NotificationContext';
import { FileUploadComponent } from './FileUploadComponent';
import { createAuditEntry, generateItemId } from '../utils';
import { AuditService } from '../services/AuditService';

export interface SummaryTabProps {
  project: Project;
  currentUser: User | null;
  allUsers: User[];
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
  setCurrentProject: React.Dispatch<React.SetStateAction<Project | null>>;
  t: LanguageContextType['t'];
  addNotification: NotificationContextType['addNotification'];
}

const SummaryTab: React.FC<SummaryTabProps> = ({
  project,
  currentUser,
  allUsers,
  setProjects,
  setCurrentProject,
  t,
  addNotification
}) => {
  const [showBalanceClearModal, setShowBalanceClearModal] = useState(false);

  // Fallback: If allUsers is empty, load users directly with real-time listener
  const [localUsers, setLocalUsers] = useState<User[]>([]);

  React.useEffect(() => {
    if (allUsers.length > 0) {
      setLocalUsers([]);
      return;
    }

    const setupLocalUsersListener = async () => {
      try {
        const { collection, onSnapshot } = await import('firebase/firestore');
        const { db } = await import('../firebaseConfig');

        const usersCollection = collection(db, 'users');

        const unsubscribe = onSnapshot(usersCollection, (snapshot) => {
          const users: User[] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as User));

          setLocalUsers(users);
        }, () => {
          setLocalUsers([]);
        });

        return unsubscribe;
      } catch (error) {
        return null;
      }
    };

    let unsubscribePromise = setupLocalUsersListener();

    return () => {
      unsubscribePromise.then(unsubscribe => {
        if (unsubscribe) {
          unsubscribe();
        }
      });
    };
  }, [allUsers.length]);

  // Use allUsers if available, otherwise use localUsers
  const usersToUse = allUsers.length > 0 ? allUsers : localUsers;
  const [balanceClearModalState, setBalanceClearModalState] = useState<{
    clearingUserId: string;
    clearAmount: string;
    clearDescription: string;
    clearAttachments: Attachment[];
  }>({ clearingUserId: '', clearAmount: '', clearDescription: '', clearAttachments: [] });

  const handleOpenBalanceClearModal = (userId: string) => {
    setBalanceClearModalState({
      clearingUserId: userId,
      clearAmount: '',
      clearDescription: '',
      clearAttachments: []
    });
    setShowBalanceClearModal(true);
  };

  // Helper to check for superseded items
  const isSuperseded = (item: Item) => {
    return item.status === 'approved' && !item.isPurchaseItem && project.items.some(i => i.isPurchaseItem === true && String(i.originalItemId) === String(item.id));
  };

  const purchaserSummaries = project.purchaserFunds.map(fund => {
    const user = usersToUse.find(u => u.id === fund.purchaserId);
    if (!user) {
      return null;
    }

    // Calculate actual spent amount from ALL relevant items (including children of groups if they meet criteria)
    // This is the true financial total for the user, used for internal calculations like balance clearing.

    // Filter items for display in the list:
    // - Individual items (not part of a group) assigned to the user and approved/unsupervised.
    // - Group items if any of their children are assigned to the user and approved/unsupervised.
    const itemsPurchasedForDisplay = project.items.filter(item => {
      if (!item.isGroup && !item.parentId) { // Individual top-level item
        return item.purchaser === user.id && (item.status === 'bought' || item.isUnsupervisedPurchase === true || (item.status === 'approved' && !isSuperseded(item)));
      }
      if (item.isGroup === true) { // Group item
        const itemIdStr = String(item.id);
        const hasRelevantChild = project.items.some(child =>
          String(child.parentId) === itemIdStr &&
          child.purchaser === user.id &&
          (child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child)))
        );
        return hasRelevantChild;
      }
      return false; // Child items are not listed directly
    });

    // Total allocated = project allocation + additional funds (including balance adjustments)
    const totalAllocated = (fund.allocatedAmount || 0) + (fund.additionalFunds || 0);

    // Calculate the total spent amount based *only* on this user's items
    let totalSpentForUserSummaryBox = 0;

    itemsPurchasedForDisplay.forEach(item => {
      let itemDisplayPrice = 0;
      if (item.isGroup) {
        const itemIdStr = String(item.id);
        // Check if group has groupTotalPrice set
        if (item.groupTotalPrice && item.groupTotalPrice > 0) {
          // Use groupTotalPrice - calculate this user's proportion
          const userChildrenInGroup = project.items.filter(child =>
            String(child.parentId) === itemIdStr &&
            child.purchaser === user.id &&
            (child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child)))
          );
          const allApprovedChildren = project.items.filter(child =>
            String(child.parentId) === itemIdStr &&
            (child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child)))
          );
          if (allApprovedChildren.length > 0 && userChildrenInGroup.length > 0) {
            const proportion = userChildrenInGroup.length / allApprovedChildren.length;
            itemDisplayPrice = (item.groupTotalPrice + (item.shippingCost || 0)) * proportion;
          }
        } else {
          // No groupTotalPrice - sum children's prices as before
          const userChildrenInGroup = project.items.filter(child =>
            String(child.parentId) === itemIdStr &&
            child.purchaser === user.id && // Only this user's children
            (child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child)))
          );
          const childrenTotalPrice = userChildrenInGroup.reduce((sum, child) => {
            return sum + ((child.price || 0) * child.quantity) + (child.shippingCost || 0);
          }, 0);

          // Calculate proportional group shipping cost
          const allApprovedChildren = project.items.filter(child =>
            String(child.parentId) === itemIdStr &&
            (child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child)))
          );
          const groupShippingProportion = allApprovedChildren.length > 0
            ? (userChildrenInGroup.length / allApprovedChildren.length)
            : 0;
          itemDisplayPrice = childrenTotalPrice + ((item.shippingCost || 0) * groupShippingProportion);
        }
      } else {
        itemDisplayPrice = ((item.price || 0) * item.quantity) + (item.shippingCost || 0);
      }
      totalSpentForUserSummaryBox += itemDisplayPrice;
    });

    // Balance for display = what they got - what is shown as spent in the summary box
    const balanceForDisplay = totalAllocated - totalSpentForUserSummaryBox;
    const hasUnbalanceForDisplay = Math.abs(balanceForDisplay) > 0.01;

    return {
      user,
      totalAllocated,
      spentAmount: totalSpentForUserSummaryBox, // Use this for the summary box display
      balance: balanceForDisplay, // Use this for the summary box display
      itemsPurchased: itemsPurchasedForDisplay,
      hasUnbalance: hasUnbalanceForDisplay, // Use this for the summary box display
    };
  }).filter(summary => summary !== null) as Array<{
    user: User,
    totalAllocated: number,
    spentAmount: number, // This is now totalSpentForUserSummaryBox
    balance: number, // This is now balanceForDisplay
    itemsPurchased: Item[],
    hasUnbalance: boolean // This is now hasUnbalanceForDisplay
  }>;

  const handleBalanceClear = async () => {
    if (!currentUser || !balanceClearModalState.clearingUserId || !balanceClearModalState.clearAmount || !balanceClearModalState.clearDescription.trim()) {
      alert(t('error.missingFields', { defaultValue: 'Please fill all required fields for balance clearing.' }));
      return;
    }

    const amount = parseFloat(balanceClearModalState.clearAmount);
    if (isNaN(amount)) {
      alert(t('error.invalidAmount', { defaultValue: 'Invalid adjustment amount.' }));
      return;
    }

    const purchaserFund = project.purchaserFunds.find(pf => pf.purchaserId === balanceClearModalState.clearingUserId);
    if (!purchaserFund) {
      alert(t('error.userNotFound', { defaultValue: 'Purchaser fund data not found.' }));
      return;
    }

    // Use the robust balance calculation from purchaserSummaries
    const userSummary = purchaserSummaries.find(s => s.user.id === balanceClearModalState.clearingUserId);
    if (!userSummary) {
      alert(t('error.userNotFound', { defaultValue: 'User summary not found.' }));
      return;
    }

    const currentBalance = userSummary.balance;
    const actualSpentFromItems = userSummary.spentAmount;

    if (currentBalance < 0 && amount < 0) {
      alert(t('fund.balance.adjustment.positive', { amount: Math.abs(currentBalance).toLocaleString(), defaultValue: `User has overspent by ${Math.abs(currentBalance).toLocaleString()}. Adjustment must be positive.` }));
      return;
    }
    if (currentBalance > 0 && amount > 0) {
      alert(t('fund.balance.adjustment.negative', { amount: currentBalance.toLocaleString(), defaultValue: `User has underspent by ${currentBalance.toLocaleString()}. Adjustment must be negative.` }));
      return;
    }

    const finalAttachments = balanceClearModalState.clearAttachments;

    try {
      const fundEntry: FundEntry = {
        id: generateItemId(),
        amount: amount,
        description: balanceClearModalState.clearDescription,
        date: new Date(),
        attachments: finalAttachments,
        createdAt: new Date(),
        purchaserId: balanceClearModalState.clearingUserId,
        type: 'balance_clear'
      };

      const updatedPurchaserFunds = project.purchaserFunds.map(pf => {
        if (pf.purchaserId === balanceClearModalState.clearingUserId) {
          return {
            ...pf,
            additionalFunds: pf.additionalFunds + amount,
            // Store spentAmount together with the availableAmount derived from
            // it, so the record always satisfies
            // available = allocated + additional - spent
            spentAmount: actualSpentFromItems,
            availableAmount: (pf.allocatedAmount + pf.additionalFunds + amount) - actualSpentFromItems,
            fundEntries: [...pf.fundEntries, fundEntry]
          };
        }
        return pf;
      });

      const auditEntry = createAuditEntry(
        currentUser.id,
        currentUser.fullName,
        'BALANCE_CLEARED',
        `Balance cleared for user ${allUsers.find(u => u.id === balanceClearModalState.clearingUserId)?.fullName || balanceClearModalState.clearingUserId} by ${amount.toLocaleString()} with reason: ${balanceClearModalState.clearDescription}`,
        undefined,
        { oldBalance: currentBalance },
        { newBalance: currentBalance + amount }
      );

      const updatedProject: Project = {
        ...project,
        purchaserFunds: updatedPurchaserFunds,
        // auditTrail: [...project.auditTrail, auditEntry] // REMOVED: Managed by AuditService
        auditTrail: [] // Clear legacy array to prevent 1MB limit check
      };

      // Add audit entry via service
      await AuditService.addAuditEntry(project.id, auditEntry);

      // CRITICAL FIX: Save to database

      const { doc, setDoc } = await import('firebase/firestore');
      const { db } = await import('../firebaseConfig');
      const { removeUndefinedValues } = await import('../utils');
      const projectDocRef = doc(db, 'projects', project.id.toString());

      try {
        const cleanedData = removeUndefinedValues(updatedProject);
        await setDoc(projectDocRef, cleanedData);
      } catch (dbError) {
        alert('Failed to save balance clearing to database. Please try again.');
        return; // Don't continue with local updates if database save failed
      }

      // Local state will be updated automatically by the realtime listener

      addNotification({
        userId: balanceClearModalState.clearingUserId,
        type: 'balance_cleared',
        title: t('notification.balance.cleared', { defaultValue: 'Balance Cleared' }),
        message: t('summary.tab.balanceClearedNotification', { userName: allUsers.find(u => u.id === balanceClearModalState.clearingUserId)?.fullName || balanceClearModalState.clearingUserId, projectName: project.name }),
        projectId: project.id,
        projectName: project.name
      });

      if (currentUser.id !== balanceClearModalState.clearingUserId) {
        addNotification({
          userId: currentUser.id,
          type: 'balance_cleared',
          title: t('notification.balance.cleared', { defaultValue: 'Balance Cleared' }),
          message: `You cleared the balance for ${allUsers.find(u => u.id === balanceClearModalState.clearingUserId)?.fullName || balanceClearModalState.clearingUserId} in project "${project.name}".`,
          projectId: project.id,
          projectName: project.name
        });
      }

      setShowBalanceClearModal(false);
      alert(t('fund.balance.cleared.success'));

    } catch (error) {
      alert(t('error.generic', { defaultValue: 'An unexpected error occurred. Please try again.' }));
    }
  };

  // Debug: Check what data we have

  // FIXED: Calculate purchaser summaries with correct spending logic

  // FIXED: Calculate purchaser summaries with correct spending logic (moved up)

  // Show summaries based on user role
  const visibleSummaries = currentUser?.role === 'admin' || currentUser?.role === 'supervisor'
    ? purchaserSummaries
    : purchaserSummaries.filter(summary => summary.user.id === currentUser?.id);

  if (visibleSummaries.length === 0) {
    return (
      <div className="summary-tab-container">
        <h2 className="summary-tab-main-title">{t('summary.tab.purchaserSummary')}</h2>
        <p className="summary-tab-no-data">{t('summary.tab.noPurchaserData')}</p>
      </div>
    );
  }

  return (
    <div className="summary-tab-container">
      <h2 className="summary-tab-main-title">{t('summary.tab.purchaserSummary')}</h2>

      {/* Project Overview section removed as per feedback */}

      {visibleSummaries.map(summary => (
        <div key={summary.user.id} className="purchaser-summary-block relative">
          {currentUser?.role === 'supervisor' && project.mode === 'กำลังจัดซื้อ' && (
            <div className="absolute top-2 right-2 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-md shadow">
              {t('summary.tab.totalSpentShort')}: ฿{summary.spentAmount.toLocaleString()}
            </div>
          )}
          <div className="purchaser-header">
            <div>
              <h3 className="purchaser-name">{summary.user.fullName}</h3>
              <p className="purchaser-details">{summary.user.team} - {t(`user.${summary.user.role}`)}</p>
            </div>
            <div className={`balance-status-pill ${summary.hasUnbalance ? 'status-unbalanced' : 'status-balanced'}`}>
              {t('summary.tab.balanceStatus')}: {summary.hasUnbalance ? t('summary.tab.unbalanced') : t('summary.tab.balanced')}
            </div>
          </div>

          <div className="fund-details-grid">
            <div className="fund-detail-item fund-allocated">
              <p className="fund-label">{t('summary.tab.totalAllocated')}</p>
              <p className="fund-value">฿{summary.totalAllocated.toLocaleString()}</p>
            </div>
            <div className="fund-detail-item fund-spent">
              <p className="fund-label">{t('summary.tab.totalSpent')}</p>
              <p className="fund-value">฿{summary.spentAmount.toLocaleString()}</p>
            </div>
            <div className={`fund-detail-item ${summary.balance >= 0 ? 'fund-remaining-positive' : 'fund-remaining-negative'}`}>
              <p className="fund-label">{t('summary.tab.remainingBalance')}</p>
              <p className="fund-value">฿{summary.balance.toLocaleString()}</p>
            </div>
          </div>

          {summary.hasUnbalance && (currentUser?.role === 'admin' || currentUser?.role === 'supervisor' || currentUser?.id === summary.user.id) && !project.status.includes('closed') && (
            <div className="clear-balance-action">
              <button
                onClick={() => handleOpenBalanceClearModal(summary.user.id)}
                className="clear-balance-button"
              >
                <RefreshCw className="button-icon" />
                {t('fund.balance.clear.button')}
              </button>
            </div>
          )}

          <div className="purchased-items-section">
            <h4 className="purchased-items-title">{t('summary.tab.itemsPurchased', { count: summary.itemsPurchased.length })}</h4>
            {summary.itemsPurchased.length > 0 ? (
              <div className="purchased-items-list">
                {summary.itemsPurchased.map(item => {
                  let displayItemName = item.name;
                  let displayQuantityUnit = `${item.quantity} ${item.unit}`;
                  let displayTotalPrice = ((item.price || 0) * item.quantity) + (item.shippingCost || 0);

                  if (item.isGroup) {
                    displayItemName = item.groupName || item.name;
                    const itemIdStr = String(item.id);

                    // Check if group has groupTotalPrice set
                    if (item.groupTotalPrice && item.groupTotalPrice > 0) {
                      displayTotalPrice = item.groupTotalPrice + (item.shippingCost || 0);
                    } else {
                      // Calculate total price for the group including its children and own shipping
                      // (exclude superseded originals or the group total double-counts
                      // an item together with its purchase clone)
                      const approvedOrUnsupervisedChildren = project.items.filter(child =>
                        String(child.parentId) === itemIdStr &&
                        (child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child)))
                      );
                      const childrenTotalPrice = approvedOrUnsupervisedChildren.reduce((sum, child) => {
                        return sum + ((child.price || 0) * child.quantity) + (child.shippingCost || 0);
                      }, 0);
                      displayTotalPrice = childrenTotalPrice + (item.shippingCost || 0);
                    }
                  }

                  return (
                    <div key={item.id} className="purchased-item-row">
                      <span className="item-name-details truncate" title={`${displayItemName} - ${(item as any).details || ''}`}>
                        {displayItemName}
                        {(item as any).details && !item.isGroup && <span className="item-detail-snippet"> - {(item as any).details.substring(0, 30)}{(item as any).details.length > 30 ? '...' : ''}</span>}
                        {item.isGroup && <span className="item-detail-snippet"> - {t('item.group.descriptionShort', { count: project.items.filter(i => String(i.parentId) === String(item.id)).length, defaultValue: `${project.items.filter(i => String(i.parentId) === String(item.id)).length} items` })}</span>}
                      </span>
                      <span className="item-qty-unit">{displayQuantityUnit}</span>
                      <span className="item-date">{(item.purchaseDate && String(item.purchaseDate) !== '') || ((item as any).createdAt && String((item as any).createdAt) !== '') ? new Date(item.purchaseDate || (item as any).createdAt).toLocaleDateString() : 'No date'}</span>
                      <span className="item-total-price">฿{displayTotalPrice.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="no-items-text">{t('item.no.items', { defaultValue: 'No items purchased by this user.' })}</p>
            )}
          </div>
        </div>
      ))}

      {showBalanceClearModal && (
        <div className="modal-overlay">
          <div className="modal-content balance-clear-modal-content w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{t('summary.tab.clearBalanceFor', { userName: allUsers.find(u => u.id === balanceClearModalState.clearingUserId)?.fullName || '' })}</h2>
              <button onClick={() => setShowBalanceClearModal(false)} className="p-2 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleBalanceClear(); }} className="space-y-4">
              <div>
                <label htmlFor="adjustmentAmount" className="block text-sm font-medium text-gray-700 mb-1">{t('fund.balance.adjustmentAmount')} *</label>
                <div className="flex items-stretch space-x-2">
                  <input
                    type="number"
                    id="adjustmentAmount"
                    value={balanceClearModalState.clearAmount}
                    onChange={(e) => setBalanceClearModalState(prev => ({ ...prev, clearAmount: e.target.value }))}
                    placeholder={t('fund.balance.adjustmentAmount.placeholder')}
                    step="0.01"
                    required
                    className="flex-grow p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const userSummary = purchaserSummaries.find(s => s.user.id === balanceClearModalState.clearingUserId);
                      if (userSummary) {
                        setBalanceClearModalState(prev => ({ ...prev, clearAmount: (-userSummary.balance).toString() }));
                      }
                    }}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm whitespace-nowrap flex items-center justify-center"
                    title={t('fund.balance.autoBalance')}
                  >
                    {t('fund.balance.autoBalance')}
                  </button>
                </div>
                {(() => {
                  const userSummary = purchaserSummaries.find(s => s.user.id === balanceClearModalState.clearingUserId);
                  if (!userSummary) return null;
                  const currentBalance = userSummary.balance;
                  if (currentBalance < 0) return <p className="text-xs text-gray-500 mt-1">{t('fund.balance.adjustment.positive', { amount: Math.abs(currentBalance).toLocaleString() })}</p>;
                  if (currentBalance > 0) return <p className="text-xs text-gray-500 mt-1">{t('fund.balance.adjustment.negative', { amount: currentBalance.toLocaleString() })}</p>;
                  return null;
                })()}
              </div>
              <div>
                <label htmlFor="clearDescription" className="block text-sm font-medium text-gray-700 mb-1">{t('fund.balance.description')} *</label>
                <textarea
                  id="clearDescription"
                  value={balanceClearModalState.clearDescription}
                  onChange={(e) => setBalanceClearModalState(prev => ({ ...prev, clearDescription: e.target.value }))}
                  placeholder={t('fund.balance.reason')}
                  rows={3}
                  required
                  className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('fund.balance.supportingDocs')}</label>
                <FileUploadComponent
                  projectId={project.id}
                  itemId={0}
                  userId={currentUser?.id || 'unknown'}
                  attachments={balanceClearModalState.clearAttachments}
                  onAttachmentsChange={(newAttachments) => setBalanceClearModalState(prev => ({ ...prev, clearAttachments: newAttachments }))}
                  t={t}
                  maxFiles={5}
                />
              </div>
              <div className="flex justify-end space-x-3 pt-3">
                <button type="button" onClick={() => setShowBalanceClearModal(false)} className="btn-secondary px-4 py-2">{t('action.cancel')}</button>
                <button type="submit" className="btn-clear-balance">{t('fund.balance.clear.button')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryTab;
